import svgPaths from "../imports/svg-03w2f4jqef";
import { motion, type Transition } from "framer-motion";
import type { ReactNode } from "react";
import BurgerArrow from "./BurgerArrow";

interface StacyBurgerProps {
  isExploded: boolean;
}

const RED = "#BA4643";
const CREAM = "#F2F1DE";

const enterTransition: Transition = {
  duration: 0.3,
  ease: [0.4, 0, 0.2, 1],
};

const leaveTransition: Transition = {
  type: "spring",
  stiffness: 550,
  damping: 18,
  mass: 1,
};

function getTransition(isExploded: boolean): Transition {
  return isExploded ? enterTransition : leaveTransition;
}

function LabelText({
  lines,
  className = "",
  lineClassNames = [],
}: {
  lines: string[];
  className?: string;
  lineClassNames?: string[];
}) {
  return (
    <div
      className={[
        "flex flex-col justify-center whitespace-nowrap",
        "font-['Diary_Notes',sans-serif] text-red-500",
        "text-[8px] leading-[8px] tracking-[0.16px]",
        "min-[380px]:text-[9px] min-[380px]:leading-[9px] min-[380px]:tracking-[0.18px]",
        "sm:text-[10px] sm:leading-[10px] sm:tracking-[0.2px]",
        "lg:text-[12px] lg:leading-[12px] lg:tracking-[0.24px]",
        className,
      ].join(" ")}
    >
      {lines.map((line, index) => (
        <p
          key={`${line}-${index}`}
          className={["mb-0", lineClassNames[index] ?? ""].join(" ")}
        >
          {line}
        </p>
      ))}
    </div>
  );
}

function FlowLabel({
  isExploded,
  side,
  inset = 0,
  children,
}: {
  isExploded: boolean;
  side: "left" | "right";
  inset?: number;
  children?: ReactNode;
}) {
  const explodedX = side === "left" ? inset : -inset;
  const hiddenX = side === "left" ? explodedX - 8 : explodedX + 8;

  return (
    <motion.div
      initial={false}
      className={[
        "pointer-events-none flex shrink-0 overflow-visible",
        side === "left" ? "justify-end" : "justify-start",
      ].join(" ")}
      animate={{
        opacity: isExploded && children ? 1 : 0,
        x: isExploded && children ? explodedX : hiddenX,
      }}
      transition={getTransition(isExploded)}
      aria-hidden={!isExploded}
    >
      <div className="flex items-center gap-2 whitespace-nowrap">
        {children}
      </div>
    </motion.div>
  );
}

function SideLabel({
  isExploded,
  side,
  lines,
  arrowDirection,
  arrowColor,
}: {
  isExploded: boolean;
  side: "left" | "right";
  lines: string[];
  arrowDirection: "left" | "right" | "left-bottom" | "right-top" | "right-bottom";
  arrowColor?: string;
}) {
  const inset = side === "left" ? 18 : 18;

  return (
    <FlowLabel isExploded={isExploded} side={side} inset={inset}>
      {side === "left" ? (
        <>
          <LabelText lines={lines} />
          <BurgerArrow direction={arrowDirection} color={arrowColor} />
        </>
      ) : (
        <>
          <BurgerArrow direction={arrowDirection} color={arrowColor} />
          <LabelText lines={lines} />
        </>
      )}
    </FlowLabel>
  );
}

function DualRightLabels({ isExploded }: { isExploded: boolean }) {
  return (
    <FlowLabel isExploded={isExploded} side="right" inset={18}>
      <div className="flex flex-col items-start gap-2">
        <div className="flex items-center gap-2">
          <BurgerArrow direction="right-top" />
          <LabelText lines={["CHEDDAR", "x2"]} />
        </div>

        <div className="flex items-center gap-2">
          <BurgerArrow direction="right-bottom" />
          <LabelText lines={["CARNE"]} />
        </div>
      </div>
    </FlowLabel>
  );
}

function LayerRow({
  isExploded,
  y,
  left,
  right,
  children,
}: {
  isExploded: boolean;
  y: number;
  left?: ReactNode;
  right?: ReactNode;
  children: ReactNode;
}) {
  return (
    <motion.div
      className="
       grid w-full justify-center items-center gap-x-1
    grid-cols-[68px_133px_68px]
    sm:grid-cols-[80px_133px_80px]
    md:grid-cols-[94px_133px_94px]
      "
      animate={{ y: isExploded ? y : 0 }}
      transition={getTransition(isExploded)}
    >
      <div className="flex h-0 justify-end overflow-visible">
        {left ?? null}
      </div>

      <div className="flex min-w-0  max-w-[133px] items-center justify-center">
        {children}
      </div>

      <div className="flex h-0 justify-start overflow-visible">
        {right ?? null}
      </div>
    </motion.div>
  );
}

export function StacyTopBun({ stroke = RED }: { stroke?: string }) {
  return (
    <div className="w-[108px] py-[2px] sm:w-[120px] md:w-[133px]">
      <div
        className="h-[36px] rounded-tl-[80px] rounded-tr-[80px] rounded-bl-[8px] rounded-br-[8px] border-4 sm:h-[40px] md:h-[43.75px]"
        style={{ borderColor: stroke }}
      />
    </div>
  );
}

export function StacySauceLayer({
  stroke = RED,
  outline = CREAM,
}: {
  stroke?: string;
  outline?: string;
}) {
  return (
    <div className="w-[108px] p-[2px] sm:w-[120px] md:w-[133px]">
      <div className="h-[7px] sm:h-[7.5px] md:h-[7.875px]">
        <svg
          className="block h-full w-full overflow-visible"
          fill="none"
          preserveAspectRatio="none"
          viewBox="0 0 133 11.2399"
        >
          <path d={svgPaths.p38f39180} stroke={outline} strokeWidth="4" />
          <path d={svgPaths.p38f39180} stroke={stroke} strokeWidth="4" />
        </svg>
      </div>
    </div>
  );
}

export function StacyMeatCheeseLayer({
  stroke = RED,
  outline = CREAM,
}: {
  stroke?: string;
  outline?: string;
}) {
  return (
    <div className="w-[108px] p-[2px] sm:w-[120px] md:w-[133px]">
      <div className="h-[16px] sm:h-[18px] md:h-[19px]">
        <svg
          className="block h-full w-full overflow-visible"
          fill="none"
          preserveAspectRatio="none"
          viewBox="0 0 133 23"
        >
          <g>
            <path d={svgPaths.p36715d00} fill={outline} />
            <path d={svgPaths.p36715d00} fill={stroke} />
          </g>
          <g>
            <path d={svgPaths.p13a95540} stroke={outline} strokeLinecap="round" strokeWidth="4" />
            <path d={svgPaths.p13a95540} stroke={stroke} strokeLinecap="round" strokeWidth="4" />
          </g>
          <g>
            <path d={svgPaths.pca7c900} stroke={outline} strokeLinecap="round" strokeWidth="4" />
            <path d={svgPaths.pca7c900} stroke={stroke} strokeLinecap="round" strokeWidth="4" />
          </g>
        </svg>
      </div>
    </div>
  );
}

export function StacyBottomBun({ stroke = RED }: { stroke?: string }) {
  return (
    <div className="w-[108px] py-[2px] sm:w-[120px] md:w-[133px]">
      <div
        className="h-[16px] rounded-[8px] border-4 sm:h-[17px] md:h-[18.75px]"
        style={{ borderColor: stroke }}
      />
    </div>
  );
}






function BadgeSVG() {
  return (
    <svg
      className="absolute inset-0 h-full w-full"
      fill="none"
      preserveAspectRatio="none"
      viewBox="0 0 75.9776 48.0182"
    >
      <g>
        <path d={svgPaths.p17fbbe40} fill={RED} />
        <path d={svgPaths.p27874a00} fill={RED} />
        <path d={svgPaths.p2a7c8dc0} fill={RED} />
        <path d={svgPaths.p3bfe46b0} fill={RED} />
        <path d={svgPaths.p3777c580} fill={RED} />
        <path d={svgPaths.p38f36c00} fill={RED} />
        <path d={svgPaths.p20300c80} fill={RED} />
        <path d={svgPaths.p9e1ff80} fill={RED} />
        <path d={svgPaths.p3517f200} fill={RED} />
        <path d={svgPaths.p198ebaf0} fill={RED} />
        <path d={svgPaths.p4db0980} fill={RED} />
        <path d={svgPaths.p3f057a00} fill={RED} />
        <path d={svgPaths.p2e05ff00} fill={RED} />
        <path d={svgPaths.p1c9e0f00} fill={RED} />
        <path d={svgPaths.p58e1200} fill={RED} />
        <path d={svgPaths.p39281e80} fill={RED} />
        <path d={svgPaths.p322ef9c0} fill={RED} />
        <path d={svgPaths.p324f6980} fill={RED} />
        <path d={svgPaths.p28596300} fill={RED} />
        <path d={svgPaths.p12272700} fill={RED} />
        <path d={svgPaths.p2b922b00} fill={RED} />
        <path d={svgPaths.p15623480} fill={RED} />
        <path d={svgPaths.p4db4580} fill={RED} />
        <path d={svgPaths.p20387000} fill={RED} />
        <path d={svgPaths.pee57f0} fill={RED} />
        <path d={svgPaths.p14f72e00} fill={RED} />
        <path d={svgPaths.p12ee4a00} fill={RED} />
        <path d={svgPaths.p11c6b480} fill={RED} />
        <path d={svgPaths.p11e9c800} fill={RED} />
        <path d={svgPaths.p1f9fbb00} fill={RED} />
        <path d={svgPaths.p8cd8c00} fill={RED} />
        <path d={svgPaths.p377e4880} fill={RED} />
        <path d={svgPaths.p3a564900} fill={RED} />
        <path d={svgPaths.p39847700} fill={RED} />
        <path d={svgPaths.p3c27d670} fill={RED} />
        <path d={svgPaths.p5201100} fill={RED} />
        <path d={svgPaths.p354dfb80} fill={RED} />
        <path d={svgPaths.p3852b680} fill={RED} />
        <path d={svgPaths.p2107d00} fill={RED} />
        <path d={svgPaths.p1fa89700} fill={RED} />
        <path d={svgPaths.p234ca700} fill={RED} />
        <path d={svgPaths.p26b93300} fill={RED} />
        <path d={svgPaths.p10d09bf2} fill={RED} />
        <path d={svgPaths.p15bc9880} fill={RED} />
        <path d={svgPaths.pfb59200} fill={RED} />
        <path d={svgPaths.p25064580} fill={RED} />
        <path d={svgPaths.p272d9400} fill={RED} />
        <path d={svgPaths.pbc10c00} fill={RED} />
        <path d={svgPaths.p3411cc00} fill={RED} />
        <path d={svgPaths.pa839d00} fill={RED} />
        <path d={svgPaths.p23a02cc0} fill={RED} />
        <path d={svgPaths.p26e0c500} fill={RED} />
        <path d={svgPaths.p12b8c800} fill={RED} />
        <path d={svgPaths.p2aae8000} fill={RED} />
        <path d={svgPaths.p6965600} fill={RED} />
        <path d={svgPaths.p31c8e900} fill={RED} />
        <path d={svgPaths.pe25d900} fill={RED} />
        <path d={svgPaths.pa1a77b0} fill={RED} />
        <path d={svgPaths.p80ee970} fill={RED} />
        <path d={svgPaths.p3a35a100} fill={RED} />
        <path d={svgPaths.paceeb00} fill={RED} />
        <path d={svgPaths.p338fe300} fill={RED} />
      </g>
    </svg>
  );
}

export default function StacyBurger({ isExploded }: StacyBurgerProps) {
  const transition = getTransition(isExploded);

  return (
    <div className="mx-auto flex w-full max-w-[420px] flex-col items-center justify-center px-2 sm:px-3">
      <div className="flex w-full flex-col items-center gap-[2px]">
        <LayerRow
          isExploded={isExploded}
          y={-30}
          right={
            <SideLabel
              isExploded={isExploded}
              side="right"
              lines={["Pan ", "BRIOCHE"]}
              arrowDirection="right"
              arrowColor="var(--red-500)"
            />
          }
        >
          <StacyTopBun />
        </LayerRow>

        <LayerRow
          isExploded={isExploded}
          y={-15}
          left={
            <SideLabel
              isExploded={isExploded}
              side="left"
              lines={["SALSA", "STACY"]}
              arrowDirection="left"
            />
          }
        >
          <StacySauceLayer />
        </LayerRow>

        <LayerRow
          isExploded={isExploded}
          y={0}
          right={<DualRightLabels isExploded={isExploded} />}
        >
          <StacyMeatCheeseLayer />
        </LayerRow>

        <LayerRow
          isExploded={isExploded}
          y={15}
          left={
            <SideLabel
              isExploded={isExploded}
              side="left"
              lines={["MAYO", "AHUMADA"]}
              arrowDirection="left-bottom"
            />
          }
        >
          <div className="-scale-y-100">
            <StacySauceLayer />
          </div>
        </LayerRow>

        <motion.div
          className="
    grid w-full justify-center items-center gap-x-1
    grid-cols-[68px_133px_68px]
    sm:grid-cols-[80px_133px_80px]
    md:grid-cols-[94px_133px_94px]
  "
          animate={{ y: isExploded ? 30 : 0 }}
          transition={transition}
        >
          <div />
          <div className="flex max-w-[133px] min-w-0 justify-center">
            <StacyBottomBun />
          </div>
          <div />
        </motion.div>
      </div>

      <motion.div
        className="mt-4 flex w-full flex-wrap items-center justify-center gap-x-3 gap-y-2 sm:gap-x-4"
        animate={{ y: isExploded ? 40 : 0 }}
        transition={transition}
      >
        <p className="text-center font-['Roboto_Mono',monospace] text-[28px] font-bold leading-none tracking-[0.06em] text-[#ba4643] sm:text-[36px] md:text-[44px] lg:text-[48px]">
          STACY
        </p>

        <div className="relative flex h-[42px] w-[68px] items-center justify-center sm:h-[46px] sm:w-[74px] md:h-[48px] md:w-[76px]">
          <BadgeSVG />
          <div className="relative z-10 flex flex-col justify-end whitespace-nowrap text-center font-['Biro_Script_Plus',sans-serif] text-[20px] leading-[16px] tracking-[0.48px] text-[#ba4643] sm:text-[22px] sm:leading-[18px] md:text-[24px] md:leading-[20px]">
            <p className="mb-0">de la</p>
            <p>casa</p>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

export function StacyBurgerClosed() {
  return <StacyBurger isExploded={false} />;
}

export function StacyBurgerExploded() {
  return <StacyBurger isExploded={true} />;
}